// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button } from '@carbon/react';
import ThemeEffects from './ThemeEffects';
import HeliosHeader from './HeliosHeader';
import IconRail from './IconRail';
import AskHeliosPanel from './AskHeliosPanel';
import AssetDetailPanelHost from './AssetDetailPanelHost';
import FeatureFlagDebugPanel from './FeatureFlagDebugPanel';
import AppRoutes from './AppRoutes';
import BackendUnreachableBanner from '../common/BackendUnreachableBanner';
import HeliosFooter from './HeliosFooter';
import { EmptyState, Loading } from './ScreenHelpers';
import { useAuth } from '../../hooks/useAuth';
import { useSlowLoading } from '../../hooks/useSlowLoading';
import { useTwinPrefetch } from '../../hooks/useTwinPrefetch';
import { useAlarmsAutoRefresh } from '../../hooks/useAlarmsAutoRefresh';
import { useApprovalsPrefetch } from '../../hooks/useApprovalsPrefetch';
import './shell.css';

/**
 * Body content for the authenticated shell. While the profile + OMS sites
 * resolve we show a loading state; once resolved we render the routes, or an
 * explicit empty/error state when the user has no sites to work with.
 */
function ShellBody() {
  const { isLoading, sites, sitesError, signInUnavailable, noAccess, refetchMe } = useAuth();
  const slowLoading = useSlowLoading(isLoading);

  // Warm the digital twin for the active site in the background, so opening
  // Knowledge Graph or Assets paints from cache instead of starting the fetch.
  useTwinPrefetch();

  // Same idea for plan approvals: start the fetch the moment the site is known,
  // so Recommendations and Optimizations paint from a shared cache entry.
  useApprovalsPrefetch();

  // Alarms and anomalies additionally poll: fast on a list route, slow off it,
  // so a state change lands in the list without a manual refresh (ADR-0013).
  useAlarmsAutoRefresh();

  if (isLoading) {
    // A request that hangs would leave this spinner up forever; past 15 s offer a
    // Retry (AB#35001).
    return (
      <>
        <Loading label="Loading your sites…" />
        {slowLoading && (
          <div className="hs-empty" role="status">
            <div>This is taking longer than usual.</div>
            <Button kind="tertiary" size="sm" onClick={() => refetchMe()} style={{ marginTop: 12 }}>
              Retry
            </Button>
          </div>
        )}
      </>
    );
  }

  if (noAccess) {
    return (
      <EmptyState big="You don't have access to EnergyFluo." sub="Contact your administrator." />
    );
  }

  // The token could not be renewed because CIAM was unreachable: the site service was
  // never called, so don't blame it (AB#35016).
  if (signInUnavailable) {
    return (
      <div className="hs-empty">
        <div className="big">Couldn&apos;t verify your sign-in.</div>
        <div>
          Helios couldn&apos;t reach the sign-in service. Check your connection and try again.
        </div>
        <Button kind="tertiary" size="sm" onClick={() => refetchMe()} style={{ marginTop: 12 }}>
          Retry
        </Button>
      </div>
    );
  }

  if (sitesError) {
    return (
      <div className="hs-empty">
        <div className="big">Couldn&apos;t load your sites.</div>
        <div>There was a problem reaching the site service. Please try again.</div>
        <Button kind="tertiary" size="sm" onClick={() => refetchMe()} style={{ marginTop: 12 }}>
          Retry
        </Button>
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <EmptyState
        big="You currently don't have any assigned sites."
        sub="Please contact your administrator."
      />
    );
  }

  return <AppRoutes />;
}

/**
 * The Carbon-based Helios shell. Replaces the old top-bar + chat/dashboard
 * layout. When unauthenticated we render the routes bare (so /login shows);
 * once authenticated the full rail + subnav + main chrome renders.
 */
export default function AppShell() {
  const isAuthenticated = useIsAuthenticated();

  return (
    <ThemeEffects>
      {!isAuthenticated ? (
        <div className="helios-shell">
          <BackendUnreachableBanner />
          <main className="helios-main">
            <AppRoutes />
          </main>
          <HeliosFooter />
        </div>
      ) : (
        <div className="helios-shell">
          <HeliosHeader />
          <BackendUnreachableBanner />
          <div className="helios-body">
            <IconRail />
            <main className="helios-main">
              <ShellBody />
            </main>
          </div>
          <HeliosFooter />
          <AskHeliosPanel />
          {/*
            Sits above the router so an open asset panel survives navigation.
            Renders null unless an asset is selected.
          */}
          <AssetDetailPanelHost />
          {/* Renders null unless DISPLAY_LOCAL_ONLY + admin — see its own comment. */}
          <FeatureFlagDebugPanel />
        </div>
      )}
    </ThemeEffects>
  );
}
